import React from "react";
import { CircularProgress, DialogContent, Menu, MenuItem, Tab, Tabs } from "@material-ui/core";
import IconOpenInBrowser from "@material-ui/icons/OpenInBrowser";
import { withTheme } from "@material-ui/styles";
import { FormattedMessage } from "react-intl";
import { withRouter } from "react-router-dom";
import FormDialog from "../custom/FormDialog";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { PageItem } from "../custom/Page";
import useGetCurrentUser from "../hooks/useGetCurrentUser";
import useHistoryWithdrawalFilters from "../hooks/useHistoryWithdrawalFilters";
import { Roles } from "../util";
import AdminWithdrawalPopUp from "./components/AdminWithdrawalPopUp";
import DetailHistoryWithdrawalPopUp from "./components/DetailHistoryWithdrawalPopUp";
import HistoryWithdrawalFilterDialog from "./components/HistoryWithdrawalFilterDialog";
import HistoryWithdrawalFilters from "./components/HistoryWithdrawalFilters";
import {
  TAB_HISTORY,
  TAB_PENDING,
  TAB_REDEMPTIONS,
  TAB_SUPPORTS_FILTER_DIALOG,
  TAB_WAITING_AUTH,
  TABLE_COLUMNS_INTENTIONS,
  TABLE_COLUMNS_REDEMPTIONS,
  tableColumnsHistory,
  tableColumnsPending,
} from "./components/HistoryWithdrawal";
import { HistorySummaryCards, PendingSummaryCards, RedemptionSummaryCards } from "./components/HistoryWithdrawalSummaryCards";
import HistoryWithdrawalTable from "./components/HistoryWithdrawalTable";

const HistoryWithdrawalRefactored = ({ location, theme }) => {
  const currentUser = useGetCurrentUser();

  const [openDialogFilter, setOpenDialogFilter] = React.useState(false);
  const [menu, setMenu] = React.useState(null);
  const [dialogWithdrawal, setDialogWithdrawal] = React.useState(false);
  const [dialogDetailWithdrawal, setDialogDetailWithdrawal] = React.useState(false);
  const [selectWithdrawal, setSelectWithdrawal] = React.useState(null);

  const initializedSearchRef = React.useRef(null);

  const {
    tabIndex,
    setTabIndex,
    isInitialLoading,
    pageSize,
    filterTaxResidence,
    tableRef,
    activeFilters,
    summaryByTab,
    getDialogFields,
    updateFilter,
    applyFilters,
    reloadTable,
    fetchTableData,
    handleDebouncedFilterField,
    handleOrderChange,
    clearInputFilters,
  } = useHistoryWithdrawalFilters({
    currentUserRole: currentUser.role,
    currentUserTaxResidence: currentUser.taxResidence,
    initialTab: TAB_PENDING,
  });

  const isActionHidden = React.useMemo(() => {
    if (tabIndex === TAB_HISTORY) return false;
    if (tabIndex === TAB_REDEMPTIONS) return true;
    if (currentUser.role === Roles.Auditor) return true;
    return false;
  }, [currentUser.role, tabIndex]);

  const tableOptions = React.useMemo(
    () => ({
      paging: false,
      toolbar: false,
      draggable: false,
      doubleHorizontalScroll: true,
      headerStyle: {
        fontSize: 12,
        color: "#FFBE2D",
        backgroundColor: "unset",
        textAlignLast: "start",
        whiteSpace: "nowrap",
      },
      rowStyle: (rowData, index) => {
        const isPendingTab = tabIndex === TAB_PENDING;
        const hasNegativeDeadline = Number(rowData.daysToLimit) < 0;

        return {
          whiteSpace: "nowrap",
          backgroundColor: isPendingTab && hasNegativeDeadline ? "#D4290D" : index % 2 ? theme.palette.secondary.light : theme.palette.secondary.dark,
          color:
            isPendingTab && rowData.daysToLimit === 0
              ? "#D4290D"
              : isPendingTab && rowData.daysToLimit === 1
                ? "#D4290D"
                : isPendingTab && rowData.daysToLimit === 2
                  ? "#FFFF00"
                  : isPendingTab && hasNegativeDeadline
                    ? "white"
                    : null,
        };
      },
    }),
    [tabIndex, theme.palette.secondary.dark, theme.palette.secondary.light],
  );

  const tableActions = React.useMemo(
    () => [
      () => ({
        icon: () => <IconOpenInBrowser color="primary" />,
        tooltip: <FormattedMessage id="app.open" />,
        onClick: (event, rowData) => {
          clearInputFilters();
          if (tabIndex === TAB_PENDING) {
            setDialogWithdrawal(true);
          } else {
            setDialogDetailWithdrawal(true);
          }
          setSelectWithdrawal(rowData);
        },
      }),
    ],
    [clearInputFilters, tabIndex],
  );

  const activeColumns = React.useMemo(() => {
    if (tabIndex === TAB_PENDING) return tableColumnsPending();
    if (tabIndex === TAB_HISTORY) return tableColumnsHistory();
    if (tabIndex === TAB_WAITING_AUTH) return TABLE_COLUMNS_INTENTIONS;
    return TABLE_COLUMNS_REDEMPTIONS;
  }, [tabIndex]);

  const activeSummary = React.useMemo(() => {
    if (tabIndex === TAB_PENDING) {
      return (
        <PendingSummaryCards
          rows={summaryByTab[TAB_PENDING].rows}
          totalCount={summaryByTab[TAB_PENDING].totalCount}
          summary={summaryByTab[TAB_PENDING].summary}
          filters={activeFilters}
          filterTaxResidence={filterTaxResidence}
          tableOptions={tableOptions}
          currentUser={currentUser}
        />
      );
    }

    if (tabIndex === TAB_HISTORY) {
      return (
        <HistorySummaryCards
          rows={summaryByTab[TAB_HISTORY].rows}
          totalCount={summaryByTab[TAB_HISTORY].totalCount}
          summary={summaryByTab[TAB_HISTORY].summary}
          filters={activeFilters}
          filterTaxResidence={filterTaxResidence}
          tableOptions={tableOptions}
          currentUser={currentUser}
        />
      );
    }

    if (tabIndex === TAB_REDEMPTIONS) {
      return (
        <RedemptionSummaryCards
          rows={summaryByTab[TAB_REDEMPTIONS].rows}
          totalCount={summaryByTab[TAB_REDEMPTIONS].totalCount}
          summary={summaryByTab[TAB_REDEMPTIONS].summary}
          tableOptions={tableOptions}
          currentUser={currentUser}
        />
      );
    }

    return null;
  }, [activeFilters, currentUser, filterTaxResidence, summaryByTab, tabIndex, tableOptions]);

  React.useEffect(() => {
    if (initializedSearchRef.current === location.search) return;
    initializedSearchRef.current = location.search;

    let tab = parseInt(location.search.substring(1), 10);
    tab = Number.isNaN(tab) ? TAB_PENDING : tab;

    clearInputFilters(tab);
    setTabIndex(tab);
    requestAnimationFrame(() => reloadTable(true));
  }, [clearInputFilters, location.search, reloadTable, setTabIndex]);

  const handleChangeTab = React.useCallback(
    (event, nextTab) => {
      clearInputFilters(nextTab);
      setTabIndex(nextTab);
    },
    [clearInputFilters, setTabIndex],
  );

  const dialogFields = getDialogFields();

  return (
    <>
      {isInitialLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1400,
            backgroundColor: theme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </div>
      )}

      <PageContainer full>
        <HeaderPageItem title="app.withdrawals" onMore={TAB_SUPPORTS_FILTER_DIALOG[tabIndex] ? (e) => setMenu(e.target) : false}>
          <Menu keepMounted anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)} component="li">
            <MenuItem
              button
              onClick={() => {
                setOpenDialogFilter(true);
                setMenu(null);
              }}
            >
              <FormattedMessage id="app.filter" />
            </MenuItem>
          </Menu>
        </HeaderPageItem>

        <PageItem>
          <Tabs style={{ height: 25, maxHeight: 25 }} indicatorColor="primary" textColor="primary" value={tabIndex} onChange={handleChangeTab} centered>
            <Tab label={<FormattedMessage id="app.pending" />} />
            <Tab label={<FormattedMessage id="app.camel.history" />} />
            <Tab label={<FormattedMessage id="app.intentions" />} />
            <Tab label={<FormattedMessage id="app.redemptions" />} />
          </Tabs>
        </PageItem>

        {activeSummary}

        <HistoryWithdrawalFilters
          filters={activeFilters}
          onFilterChange={handleDebouncedFilterField}
          idFieldLabel={tabIndex === TAB_REDEMPTIONS ? "app.search.contractId" : "app.search.withdrawal"}
        />

        <HistoryWithdrawalTable
          tableRef={tableRef}
          tableKey={`withdraw-table-${tabIndex}`}
          data={fetchTableData}
          columns={activeColumns}
          onOrderChange={handleOrderChange}
          showTableOverlayLoading={!isInitialLoading}
          overlayBackgroundColor={theme.palette.background.default}
          pageSize={pageSize}
          tableOptions={tableOptions}
          actions={!isActionHidden ? tableActions : null}
          currentUser={currentUser}
        />
      </PageContainer>

      <HistoryWithdrawalFilterDialog
        title={<FormattedMessage id="app.filter" />}
        open={openDialogFilter}
        onClose={() => setOpenDialogFilter(false)}
        fields={dialogFields}
        values={activeFilters}
        onFieldChange={updateFilter}
        onAction={() => {
          applyFilters();
          setOpenDialogFilter(false);
        }}
      />

      <FormDialog
        title="app.withdraw.nr"
        values={selectWithdrawal ? selectWithdrawal.id : ""}
        open={dialogWithdrawal}
        variant="secondary"
        noHeader
        onClose={() => {
          setDialogWithdrawal(false);
          setSelectWithdrawal(null);
          reloadTable(false);
        }}
      >
        <DialogContent>
          <AdminWithdrawalPopUp
            selectWithdrawal={selectWithdrawal}
            onActionComplete={() => {
              setDialogWithdrawal(false);
              setSelectWithdrawal(null);
              reloadTable(false);
            }}
          />
        </DialogContent>
      </FormDialog>

      <FormDialog
        title="app.withdraw.nr"
        values={selectWithdrawal ? selectWithdrawal.id : ""}
        open={dialogDetailWithdrawal}
        variant="secondary"
        noHeader
        onClose={() => {
          setDialogDetailWithdrawal(false);
          setSelectWithdrawal(null);
          reloadTable(false);
        }}
      >
        <DialogContent>
          <DetailHistoryWithdrawalPopUp
            selectWithdrawal={selectWithdrawal}
            onActionComplete={() => {
              setDialogDetailWithdrawal(false);
              setSelectWithdrawal(null);
              reloadTable(false);
            }}
          />
        </DialogContent>
      </FormDialog>
    </>
  );
};

export default withRouter(withTheme(HistoryWithdrawalRefactored));
